import { useState, useEffect } from "react";
import "./App.css";
import Sidebar from "./Components/Sidebar";
import Header from "./Components/Header";
import ClientManagement from "./Tables/ClientManagement";
import LedgerTable from "./Tables/LedgerTable";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

import InvoiceManagement from "./Tables/InvoiceManagement";
import Login from "./Components/Login";
import SubscriptionPlan from "./Components/SubscriptionPlan";
import SystemUsers from "./Tables/SystemUsers";
import SuperAdmin from "./Tables/SuperAdmin";
import UserForm from "./Forms/UserForm";
import FamilyGroup from "./Tables/FamilyGroup";
import FamilyGroupForm from "./Forms/FamilyGroupForm";
import AdminForm from "./Forms/AdminForm";
import EditFirms from "./EditForms/EditFirms";
import CaAdmins from "./Tables/CaAdmins";
import CaAdminForm from "./Forms/CaAdminForm";
import EditAdmins from "./EditForms/EditAdmins";
import SubsidiaryCard from "./Tables/Subsidary";
import SubsidaryForm from "./Forms/SubsidaryForm";
import EditSubsidary from "./EditForms/EditSubsidary";
import ClientManagementForm from "./Forms/ClientManagementForm";
import EditClientManagement from "./EditForms/EditClientManagement";
import ServiceMaster from "./Tables/ServiceMaster";
import ServiceMasterForm from "./Forms/ServiceMasterForm";
import EditServiceMaster from "./EditForms/EditServiceMaster";
import Password from "./Components/Password";
import InvoiceForm from "./Forms/InvoiceForm";
import FeeCalculator from "./Forms/FeeCalculator";
import FeeCalculations from "./Tables/FeeCalculations";
import RecordPayement from "./Tables/RecordPayement";
import RecordPaymentForm from "./Forms/RecordPayementForm";
import ChecklistTemplateForm from "./Forms/ChecklistTemplateForm";
import ViewLedger from "./View/ViewLedger";
import Visitor from "./Tables/Visitor";
import VisitorForm from "./Forms/VisitorForm";
import EditRecordPayement from "./EditForms/EditRecordPayement";
import EditInvoice from "./EditForms/EditInvoice";
import ForgotPassword from "./Components/ForgotPassword";
import OtpVerification from "./Components/OtpVerification";
import ConfirmPassword from "./Components/ConfirmPassword";
import ViewInvoice from "./View/ViewInvoice";
import InvoiceHistory from "./View/InvoiceHistory";
import PayementHistory from "./View/PayementHistory";
import ChecklistView from "./View/ChecklistView";
import EditSystemUsers from "./EditForms/EditSystemUsers";
import ChangePassword from "./Popup/ChangePassword";
import TodaysDue from "./Tables/TodaysDue";
import ReminderManagement from "./Tables/ReminderManagement";
import FamilyGroupReminder from "./Tables/FamilyGroupReminder";
import ReminderHistory from "./View/ReminderHistory";
import ArchivedFirms from "./ArchivedTables/ArchivedFirms";
import ArchivedClients from "./ArchivedTables/ArchivedClients";
import ArchivedSubsidiary from "./ArchivedTables/ArchivedSubsidiary";
import ArchivedSystemUsers from "./ArchivedTables/ArchivedSystemUser";
import ArchivedInvoice from "./ArchivedTables/ArchivedInvoice";
import Dashboard from "./Dashboard";
import SubsidiaryReport from "./Tables/SubsidiaryReport";
import InvoiceReport from "./Tables/InvoiceReport";
import PaymentReport from "./Tables/PaymentReport";
import Checklist from "./Tables/Checklist";
import DeedsPage from "./pages/Deeds/DeedsPage";
import DeedFillPage from "./pages/Deeds/DeedFillPage";
import DeedFinalPreviewPage from "./pages/Deeds/DeedFinalPreviewPage";
import SettingsPage from "./pages/SettingsPage";

const clearAuthStorage = () => {
  localStorage.removeItem("authKey");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("name");
  localStorage.removeItem("email");
  localStorage.removeItem("user_role");
};

const isTokenValid = (token) => {
  if (!token || typeof token !== "string") return false;

  try {
    const parts = token.split(".");
    if (parts.length !== 3) return false;

    let payloadBase64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const paddingNeeded = payloadBase64.length % 4;
    if (paddingNeeded) {
      payloadBase64 += "=".repeat(4 - paddingNeeded);
    }
    const payloadJson = atob(payloadBase64);
    const payload = JSON.parse(payloadJson);
    const exp = Number(payload?.exp);

    if (!Number.isFinite(exp)) return false;

    const nowInSeconds = Math.floor(Date.now() / 1000);
    return exp > nowInSeconds;
  } catch (_error) {
    return false;
  }
};

const App = () => {
  const [loading, setLoading] = useState(true);
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
  const [loggedIn, setLoggedIn] = useState(false);
  const [name, setName] = useState("");

  useEffect(() => {
    const authKey = localStorage.getItem("authKey");
    const storedName = localStorage.getItem("name");

    if (isTokenValid(authKey)) {
      setLoggedIn(true);
      setName(storedName || "");
    } else {
      setLoggedIn(false);
      setName("");
      if (authKey) {
        clearAuthStorage();
      }
    }

    setTimeout(() => {
      setLoading(false);
    }, 1000);

    const handleAppLogout = () => {
      setLoggedIn(false);
      setName("");
      clearAuthStorage();
    };

    window.addEventListener("app:logout", handleAppLogout);
    return () => window.removeEventListener("app:logout", handleAppLogout);
  }, []);

  const handleSidebarToggle = (expanded) => {
    setIsSidebarExpanded(expanded);
  };

  const handleLogin = (authKey, name, email, user_role, refresh_token) => {
    console.log("=== APP.JSX handleLogin ===");
    console.log("Login handler invoked");

    if (authKey && isTokenValid(authKey) && name && email && user_role) {
      localStorage.setItem("authKey", authKey);
      localStorage.setItem("name", name);
      localStorage.setItem("email", email);
      localStorage.setItem("user_role", user_role);

      // Store refresh_token if provided
      if (refresh_token) {
        localStorage.setItem("refresh_token", refresh_token);
      
      } else {
        console.warn("No refresh token provided during login");
      }

      setLoggedIn(true);
      setName(name);
    } else {
      clearAuthStorage();
      setLoggedIn(false);
      setName("");
      console.error("Invalid login response. Missing required fields.");
    }
  };

  return (
    <Router>
      {loading ? (
        <div>Loading...</div>
      ) : (
        <Routes>
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/api/complete_registration" element={<Password />} />
          <Route path="/login" element={<Login onLogin={handleLogin} />} />
          <Route path="/subscription-plan" element={<SubscriptionPlan />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/otp-verification" element={<OtpVerification />} />
          <Route path="/confirm-password" element={<ConfirmPassword />} />

          {!loggedIn && (
            <>
              <Route path="*" element={<Navigate to="/login" replace />} />
            </>
          )}

          {loggedIn && (
            <>
              <Route
                path="/dashboard"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <Dashboard isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />

              <Route
                path="/subsidiary-report"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <SubsidiaryReport isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />

              <Route
                path="/invoice-report"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <InvoiceReport isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />

              <Route
                path="/payment-report"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <PaymentReport isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />

              <Route
                path="/settings/*"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <SettingsPage isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />

              <Route
                path="/client-management"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <ClientManagement isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />

              <Route
                path="/Ledger-management"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <LedgerTable isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />
              <Route
                path="/view-Ledger/:client_id"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <ViewLedger isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />
              <Route
                path="/Invoice-management"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <InvoiceManagement isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />

              <Route
                path="/system-users"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <SystemUsers isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />
              <Route
                path="/users-form"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <UserForm isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />

              <Route
                path="/client-management-form"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <ClientManagementForm
                      isSidebarExpanded={isSidebarExpanded}
                    />
                  </>
                }
              />

              <Route
                path="/record-payement-form"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <RecordPaymentForm isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />
              <Route
                path="/super-Admin"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <SuperAdmin isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />
              
              <Route
                path="/Edit-Firms/:firm_id"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <EditFirms isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />
              <Route
                path="/CA-Admin"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <CaAdmins isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />
              <Route
                path="/CA-Admin-form"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <CaAdminForm isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />
              <Route
                path="/edit-Admin/:Admin_id"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <EditAdmins isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />

              <Route
                path="/Subsidary"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <SubsidiaryCard isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />
              <Route
                path="/Visitor"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <Visitor isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />
              <Route
                path="/Visitor-Form"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <VisitorForm isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />

              <Route
                path="/new-subsidary"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <SubsidaryForm isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />
              <Route
                path="/Edit-subsidary/:subsidiary_id"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <EditSubsidary isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />

              <Route
                path="/Service-Master"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <ServiceMaster isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />
              <Route
                path="/edit-client-management/:client_id"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <EditClientManagement
                      isSidebarExpanded={isSidebarExpanded}
                    />
                  </>
                }
              />
              <Route
                path="/Service-Master-Form"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <ServiceMasterForm isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />
              
              <Route
                path="/edit-master-form/:service_id"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <EditServiceMaster isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />
              <Route
  path="/family-groups"
  element={
    <>
      <Sidebar onSidebarToggle={handleSidebarToggle} />
      <Header isSidebarExpanded={isSidebarExpanded} />
      <FamilyGroup isSidebarExpanded={isSidebarExpanded} />
    </>
  }
/>


<Route
  path="/family-group-form"
  element={
    <>
      <Sidebar onSidebarToggle={handleSidebarToggle} />
      <Header isSidebarExpanded={isSidebarExpanded} />
      <FamilyGroupForm isSidebarExpanded={isSidebarExpanded} />
    </>
  }
/>

   
        <Route
          path="/edit-family-group/:id"
          element={
            <>
              <Sidebar onSidebarToggle={handleSidebarToggle} />
              <Header isSidebarExpanded={isSidebarExpanded} />
              <FamilyGroupForm isSidebarExpanded={isSidebarExpanded} />
            </>
          }
        />
              <Route
                path="/Invoice-Form"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <InvoiceForm isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />
              <Route
                path="/Fee-Calculator"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <FeeCalculations isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />
              <Route
                path="/Fee-Calculator/new"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <FeeCalculator isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />
              <Route
                path="/record-payement"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <RecordPayement isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />
              <Route
                path="/edit-record-payement/:payment_id"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <EditRecordPayement isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />
              <Route
                path="/edit-invoice/:invoice_id"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <EditInvoice isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />
              <Route
                path="/edit-system-user/:user_id"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <EditSystemUsers isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />
              <Route
                path="/Change-Password/:user_id"
                element={
                  <>
                    <ChangePassword isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />

              <Route
                path="/Todays-due"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <TodaysDue isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />
              <Route
                path="/all-due"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <TodaysDue isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />
              <Route
                path="/View-Invoice/:invoice_id"
                element={
                  <>
                    {/* <Sidebar onSidebarToggle={handleSidebarToggle} />
                  <Header isSidebarExpanded={isSidebarExpanded} /> */}
                    <ViewInvoice isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />

              <Route
                path="/Payement-history/:payment_id"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <PayementHistory isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />

              <Route
                path="/Invoice-log/:invoice_id"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <InvoiceHistory isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />
              <Route
                path="/individual-clients"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <ReminderManagement isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />
              <Route
                path="/checklist-form"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <ChecklistTemplateForm
                      isSidebarExpanded={isSidebarExpanded}
                    />
                  </>
                }
              />

              <Route
                path="/checklist"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <Checklist isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />
              <Route
                path="/checklist-view/:checklist_id"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <ChecklistView isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />
              <Route
                path="/edit-checklist/:checklist_id"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <ChecklistTemplateForm
                      isSidebarExpanded={isSidebarExpanded}
                    />
                  </>
                }
              />
              <Route
                path="/family-group"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <FamilyGroupReminder isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />
              <Route
                path="/Reminder-history/:invoice_id"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <ReminderHistory isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />
              <Route
                path="/archived-firms"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <ArchivedFirms isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />
              <Route
                path="/archived-clients"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <ArchivedClients isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />
              <Route
                path="/archived-subsidary"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <ArchivedSubsidiary isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />
              <Route
                path="/archived-invoice"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <ArchivedInvoice isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />
              <Route
                path="/archived-users"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <ArchivedSystemUsers
                      isSidebarExpanded={isSidebarExpanded}
                    />
                  </>
                }
              />

              <Route
                path="/deeds"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <DeedsPage isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />

              <Route
                path="/deeds/fill/:instance_id"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <DeedFillPage isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />

              <Route
                path="/deeds/:deed_instance_id/final"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <DeedFinalPreviewPage
                      isSidebarExpanded={isSidebarExpanded}
                    />
                  </>
                }
              />

              <Route
                path="/"
                element={
                  <>
                    <Sidebar onSidebarToggle={handleSidebarToggle} />
                    <Header isSidebarExpanded={isSidebarExpanded} />
                    <ClientManagement isSidebarExpanded={isSidebarExpanded} />
                  </>
                }
              />

              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </>
          )}
        </Routes>
      )}
    </Router>
  );
};

const Layout = ({ children, isSidebarExpanded, onSidebarToggle }) => {
  return (
    <div>
      <Sidebar
        onSidebarToggle={onSidebarToggle}
        isSidebarExpanded={isSidebarExpanded}
      />
      <div
        className={`content-container ${isSidebarExpanded ? "" : "collapsed"}`}
      >
        <Header isSidebarExpanded={isSidebarExpanded} />
        {children}
      </div>
    </div>
  );
};

export default App;
